//#region IMPORTS
import {
  useForm,
  Controller,
  FieldValues,
  useFieldArray,
} from "react-hook-form";
import {
  TrashIcon,
  PlusCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import { Fragment, useEffect } from "react";
import MainFieldArrayFormComboBox from "./MainFieldArrayFormComboBox";
import classNames from "../../helpers/classNames";
//#endregion

//#region INTERFACE
interface Props {
  onSubmitHandler: (items: string[]) => void;
  initialItems?: string[];
  propertyKey: string;
  fieldName: string;
  required?: boolean;
  options?: string[];
  cancel: () => void;
}
//#endregion

export default function MainFieldArrayForm({
  onSubmitHandler,
  initialItems,
  propertyKey,
  fieldName,
  required = true,
  options = [],
  cancel,
}: Props) {
  //#region SETUP
  const {
    reset,
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
  } = useForm({
    mode: "all",
    defaultValues: {
      [fieldName]: [{ propertyKey: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldName,
  });
  //#endregion

  //#region LOGIC
  useEffect(() => {
    if (initialItems && initialItems.length > 0) {
      setValue(
        fieldName,
        initialItems.map((x) => {
          const obj: Record<string, string> = {};
          obj[propertyKey] = x;
          return obj as any;
        })
      );
    }
  }, [initialItems, setValue, fieldName, propertyKey]);

  const submitForm = (data: FieldValues) => {
    const itemsArray: string[] = data[fieldName].map(
      (item: { propertyKey: string }) =>
        (item as Record<string, any>)[propertyKey]
    );
    onSubmitHandler(itemsArray);
  };
  //#endregion
  //#region RENDER
  return (
    <main className="space-y-8">
      <header>
        <h2 className="text-base font-semibold leading-7 text-gray-900">
          Add items
        </h2>
      </header>

      <form onSubmit={handleSubmit(submitForm)}>
        <div className="min-h-[300px] flex flex-col justify-between gap-y-6">
          <ul>
            {fields.map((item, index) => (
              <li key={item.id} className="mt-4">
                <fieldset className="flex items-center gap-4">
                  <Controller
                    name={`${fieldName}.${index}.${propertyKey}`}
                    control={control}
                    defaultValue={
                      (item as Record<string, any>)[propertyKey] || ""
                    }
                    rules={{
                      required: required ? "This field is required" : false,
                    }}
                    render={({ field }) => (
                      <MainFieldArrayFormComboBox
                        options={options.filter(
                          (opt) =>
                            !getValues(fieldName).some(
                              (ct: any) => ct[propertyKey] === opt
                            )
                        )}
                        {...field}
                      />
                    )}
                  />
                  <div className="w-44">
                    {(errors[fieldName] as any)?.[index]?.[propertyKey] ? (
                      <div className="flex items-center gap-2 text-sm font-medium leading-6 text-red-900">
                        <ExclamationCircleIcon className="h-5 w-5" />
                        This cannot be empty
                      </div>
                    ) : index !== 0 ||
                      (index === 0 && getValues(fieldName).length > 1) ? (
                      <Fragment>
                        <button
                          type="button"
                          className="flex w-full items-center justify-center gap-x-1.5 rounded-md bg-rose-100 px-3 py-2 text-sm font-semibold text-rose-800 shadow-xs hover:bg-rose-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-600"
                          onClick={() => remove(index)}
                        >
                          <TrashIcon className="h-5 w-5 text-white-400" />
                          remove item
                        </button>
                      </Fragment>
                    ) : null}
                  </div>
                </fieldset>
              </li>
            ))}
          </ul>

          <div className="flex items-center justify-between gap-x-6">
            <div className="flex items-center gap-x-4">
              <button
                onClick={() => append({ [propertyKey]: "" })}
                type="button"
                className="inline-flex items-center gap-x-1.5 rounded-md bg-sky-800 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-sky-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-800"
              >
                <PlusCircleIcon
                  aria-hidden="true"
                  className="-ml-0.5 h-5 w-5"
                />
                add item
              </button>

              <button
                onClick={() => reset({ [fieldName]: [{ [propertyKey]: "" }] })}
                type="button"
                className="rounded-md bg-sky-50 px-3 py-2 text-sm font-semibold text-sky-800 shadow-xs hover:bg-sky-100"
              >
                reset all
              </button>
            </div>

            <div className="flex items-center gap-x-6">
              <button
                onClick={cancel}
                type="button"
                className="text-sm font-semibold leading-6 text-gray-900"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={!isValid}
                className={classNames(
                  isValid
                    ? "bg-emerald-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 hover:bg-emerald-500 focus-visible:outline"
                    : "bg-gray-600 opacity-40",
                  "rounded-md  px-3 py-2 text-sm font-semibold text-white shadow-sm"
                )}
              >
                {isSubmitting ? "saving..." : "save items"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </main>
  );
  //#endregion
}
